/**
 * Session-only entry scope carried into a scenario by "Create scenario from
 * selection" (SE-8343 / SE-8345, PV12 / PV15, R07). Lives in its own module
 * so `scenarioStore` can sync it on every active-id change without an import
 * cycle through `scenarioNavStore` (which imports the scenario store for the
 * create-modal request).
 * @module features/squad-planning/stores/scenarioEntryScopeStore
 */

import { makeVar, useReactiveVar } from "@apollo/client";

/**
 * Session-only entry scope carried into a scenario by "Create scenario from
 * selection" (SE-8343, PV12 / R07): the selected player ids plus their
 * provenance. Never persisted, never stored on the scenario; it applies only
 * while the scenario it was carried into is the active one, and is gone after
 * a reload or when the user clears it.
 */
export interface ScenarioEntryScope {
  readonly scenarioId: string;
  /** Unique player ids, first-source order. */
  readonly playerIds: readonly string[];
  readonly sources: readonly {
    readonly watchlistId: string;
    readonly listName: string;
    readonly count: number;
  }[];
}

/** The entry scope of the scenario the user just created, if any. */
export const scenarioEntryScopeVar = makeVar<ScenarioEntryScope | null>(null);

/**
 * Records the entry scope for a freshly created scenario.
 * @param scope - Selected ids and their provenance
 */
export function setScenarioEntryScope(scope: ScenarioEntryScope): void {
  scenarioEntryScopeVar(scope);
}

/** Drops the entry scope. */
export function clearScenarioEntryScope(): void {
  scenarioEntryScopeVar(null);
}

/**
 * Keeps the scope in step with the active scenario (R07 S8, SE-8345): once
 * the user LEAVES the scenario the scope was carried into, the scope is gone
 * for good — coming back must not restore a stale Selected group. A scope
 * recorded while another scenario is still active (the confirm-discard path
 * defers the activation of the created one) is retained until that scenario
 * has been active and left.
 *
 * Called by the scenario store on every active-id change; it does not
 * subscribe on its own so suites that mock the store wholesale stay hermetic.
 * @param previousActiveId - Active scenario id before the change
 * @param nextActiveId - Active scenario id after the change
 */
export function syncScenarioEntryScopeToActive(
  previousActiveId: string | null,
  nextActiveId: string | null
): void {
  const scope = scenarioEntryScopeVar();
  if (scope == null) return;
  if (
    previousActiveId === scope.scenarioId &&
    nextActiveId !== scope.scenarioId
  ) {
    scenarioEntryScopeVar(null);
  }
}

/**
 * The entry scope that applies to the active scenario, or null.
 * @param activeScenarioId - Active scenario id, null on Current Squad
 * @returns The scope when it belongs to the active scenario
 */
export function useScenarioEntryScope(
  activeScenarioId: string | null
): ScenarioEntryScope | null {
  const scope = useReactiveVar(scenarioEntryScopeVar);
  return scope != null &&
    activeScenarioId != null &&
    scope.scenarioId === activeScenarioId
    ? scope
    : null;
}
